import { useState, useCallback, useRef, useEffect } from 'react';
import dataModel from '../../../../../models/DataModel';

export const useProjectTypeNavState = ({
  project_type,
  selectedProjectTemplate,
  selectedProjectType,
  selected,
  editProjectType,
  handleDeleteTemplate,
  handleAddProjectTemplate,
  handleUpdateProjectTypeTemplates,
  allTypes,
  textMap,
}) => {
  // State management
  const [menu, setMenu] = useState(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [userHasToggled, setUserHasToggled] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [editProjectTypeState, setEditProjectTypeState] = useState(null);
  const [addingTemplate, setAddingTemplate] = useState(false);
  const [ignoreClose, setIgnoreClose] = useState(false);
  const [ignoreSelection, setIgnoreSelection] = useState(false);

  // Refs
  const nameEditRef = useRef(null);
  const addTemplateRef = useRef(null);
  const parentRef = useRef(null);

  // Event handlers
  const handleSetTitleElement = useCallback((element) => {
    if (element) {
      element.focus();
    }
  }, []);

  const handleEditCancel = useCallback(() => {
    setEditingName(false);
    setEditProjectTypeState(null);
  }, []);

  const handleClickOutside = useCallback((e) => {
    if (editingName && nameEditRef.current && !nameEditRef.current.contains(e.target)) {
      handleEditCancel();
    }
  }, [editingName, handleEditCancel]);

  const handleCloseMenu = useCallback(() => {
    if (menu) {
      if (!ignoreClose) {
        setIgnoreSelection(true);
      }
      setIgnoreClose(false);
      setMenu(null);
    }
  }, [menu, ignoreClose]);

  const doDeleteProjectType = useCallback(() => {
    const idToDelete = project_type.id;
    dataModel.projectTypesDAO.deleteProjectType(idToDelete);
    project_type.deleted = true;
  }, [project_type]);

  const handleEditTemplateName = useCallback((template) => {
    dataModel.projectTemplatesDAO.updateProjectTemplate(template).then(() => {
      dataModel.displayInfoErrorMessage('The Template has been successfully renamed.', 4000, 1);
      handleUpdateProjectTypeTemplates(template, project_type.id);
    }).catch((error) => dataModel.displayInfoErrorMessage('Something went wrong.', 4000, 2));
  }, []);

  const doDeleteProjectTemplate = useCallback((id, group_identifier, is_template) => {
    dataModel.projectTemplatesDAO.deleteProjectTemplate(id, is_template).then(deletedTemplate => {
      handleDeleteTemplate(id, group_identifier, is_template);
    });
  }, [handleDeleteTemplate]);

  const handleDeleteProjectTemplate = useCallback((id, group_identifier, is_template) => {
    dataModel.showGlobalModal('SYSTEM_POPUP', {
      message: textMap.projectTemplateArchiveQuestion,
      height: 184,
      width: 300,
      cancelButtonText: textMap.cancel,
      okButtonText: textMap.deleteProjectType,
      handleOwnerOk: () => {
        doDeleteProjectTemplate(id, group_identifier, is_template);
      },
    });
  }, [textMap, doDeleteProjectTemplate]);

  const handleDeleteProjectType = useCallback(() => {
    if (project_type.project_templates && project_type.project_templates.length > 0) {
      dataModel.showGlobalModal('SYSTEM_POPUP', {
        message: 'Project Type ' + project_type.name + ' cannot be deleted because it contains Project Templates.',
        width: 300,
        height: 184,
        oneButton: true,
      });
    } else {
      dataModel.showGlobalModal('SYSTEM_POPUP', {
        message: textMap.projectTypeArchiveQuestion,
        height: 184,
        width: 300,
        cancelButtonText: textMap.cancel,
        okButtonText: textMap.deleteProjectType,
        handleOwnerOk: () => {
          doDeleteProjectType();
        },
      });
    }
  }, [project_type, textMap, doDeleteProjectType]);

  const handleEditProjectType = useCallback(() => {
    let editProjectTypeData = Object.assign({}, project_type, {});
    setEditingName(true);
    setEditProjectTypeState(editProjectTypeData);
  }, [project_type]);

  const handleMenuSelection = useCallback((type) => {
    switch (type) {
    case 'delete':
      handleDeleteProjectType();
      break;
    case 'edit':
      handleEditProjectType();
      break;
    default:
      break;
    }
    if (type) {
      setIgnoreSelection(false);
      setIgnoreClose(true);
    }
  }, [handleDeleteProjectType, handleEditProjectType]);

  const handleValidation = useCallback((string) => {
    let pass = false;
    if (string && string.length > 0 && string.trim() !== '') {
      pass = true;
    }
    return pass;
  }, []);

  const handleNameChange = useCallback((e) => {
    const editedProjectType = Object.assign({}, editProjectTypeState, { name: e.target.value });
    setEditProjectTypeState(editedProjectType);
  }, [editProjectTypeState]);

  const handleIfProjectTypeAlreadyExists = useCallback((string) => {
    let allProjectTypes = allTypes;
    return allProjectTypes.find(x => { return x.name.toUpperCase() === string.toUpperCase(); });
  }, [allTypes]);

  const handleProjectTypeUpdate = useCallback(() => {
    let exist = handleIfProjectTypeAlreadyExists(editProjectTypeState.name);
    if (!exist) {
      let validated = handleValidation(editProjectTypeState.name);
      if (validated) {
        if (editProjectTypeState) {
          editProjectType(editProjectTypeState);
          setEditingName(false);
          setEditProjectTypeState(null);
        }
      } else {
        dataModel.displayInfoErrorMessage(textMap.ProjectTypeTypeNameRequired, 4000, 2);
      }
    }
  }, [editProjectTypeState, handleIfProjectTypeAlreadyExists, handleValidation, textMap, editProjectType]);

  const handleEditKeyDown = useCallback((e) => {
    if (e.keyCode === 13 || e.keyCode === 40) {
      handleProjectTypeUpdate();
    } else if (e.keyCode === 27) {
      handleEditCancel();
    }
  }, [handleProjectTypeUpdate, handleEditCancel]);

  const handleInputClick = useCallback((e) => {
    e.stopPropagation();
  }, []);

  const toggleExpanded = useCallback(() => {
    setIsExpanded(prevState => !prevState);
    setUserHasToggled(true);
  }, []);

  const handleAddProjectTemplateLocal = useCallback((e) => {
    e.stopPropagation();
    setAddingTemplate(true);
  }, []);

  const handleCloseAddCard = useCallback(() => {
    setAddingTemplate(false);
  }, []);

  // Effects
  useEffect(() => {
    document.addEventListener('click', handleClickOutside, false);
    return () => {
      document.removeEventListener('click', handleClickOutside, false);
    };
  }, [handleClickOutside]);

  return {
    // State
    menu,
    isExpanded,
    userHasToggled,
    editingName,
    editProjectTypeState,
    addingTemplate,
    ignoreClose,
    ignoreSelection,
    
    // Refs
    nameEditRef,
    addTemplateRef,
    parentRef,
    
    // Setters
    setMenu,
    setIsExpanded,
    setEditingName,
    setEditProjectTypeState,
    setAddingTemplate,
    setIgnoreClose,
    setIgnoreSelection,
    
    // Handlers
    handleSetTitleElement,
    handleClickOutside,
    handleCloseMenu,
    doDeleteProjectType,
    handleEditTemplateName,
    doDeleteProjectTemplate,
    handleDeleteProjectTemplate,
    handleDeleteProjectType,
    handleEditProjectType,
    handleMenuSelection,
    handleValidation,
    handleNameChange,
    handleIfProjectTypeAlreadyExists,
    handleProjectTypeUpdate,
    handleEditKeyDown,
    handleInputClick,
    toggleExpanded,
    handleAddProjectTemplateLocal,
    handleCloseAddCard,
  };
};



// WEBPACK FOOTER //
// ./src/containers/ProjectTemplatesContainer/ProjectTypesNav/ProjectTypeNavCard/hooks/useProjectTypeNavState.js